import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock, Mail, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="grain relative hidden flex-col justify-between overflow-hidden bg-[#1C1C1E] p-12 lg:flex">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#D52B3B]/20 blur-3xl" />
        <div className="absolute -bottom-40 left-10 h-96 w-96 rounded-full bg-[#661D1D]/30 blur-3xl" />
        <img src="/logo-epg.png" alt="EP Group" className="relative w-[420px] max-w-full" data-testid="login-logo" />
        <div className="relative max-w-lg fade-up">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D52B3B]">EU Deforestation Regulation</p>
          <h1 className="mt-4 text-4xl font-semibold leading-tight tracking-tight text-white sm:text-5xl">
            EUDR Compliance
          </h1>
          <p className="mt-5 text-base text-slate-400">
            Annex I scope matching across 13 plants, UK and FR entities — ready for supplier evidence, risk and due diligence statements.
          </p>
        </div>
        <p className="relative text-xs text-slate-500">EUDR Compass by EP Group · applies from 30 Dec 2026</p>
      </div>
      <div className="flex items-center justify-center bg-white px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <img src="/logo-epg-mark.png" alt="EP Group" className="mb-8 h-10 rounded bg-[#1C1C1E] p-2 lg:hidden" />
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Sign in to EUDR Compass</h2>
          <p className="mt-1 text-sm text-slate-500">Use your company email and password.</p>
          <label className="mt-8 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 pl-9" placeholder="name@company.com" />
          </div>
          <label className="mt-5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
          <div className="relative mt-1.5">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pl-9" placeholder="••••••••" />
          </div>
          {error && (
            <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">
              {error}
            </p>
          )}
          <button
            data-testid="login-submit-button"
            disabled={busy}
            className="mt-7 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#D52B3B] font-medium text-white transition-colors duration-150 hover:bg-[#661D1D] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
